'use client';

import { useLivePlays } from '@/hooks/useLiveStats';
import { ballOnLabel, downAndDistanceLabel, optionalClockLabel, periodLabel } from '@/lib/liveStats';
import { cn } from '@/lib/utils';
import { OtnGame } from '@models/ot_nationals/otn_game';
import { OtnDrive, OtnDriveOutline, OtnPublicPlay } from '@models/ot_nationals/otn_live_stats';
import { Urls } from '@urls';
import Image from 'next/image';
import { useMemo } from 'react';

type PlayByPlayProps = {
	game: OtnGame;
	initialDrives: OtnDrive[];
	/** The page is a ?hidden=true preview, so a re-fetch must ask for the hidden game too. */
	preview?: boolean;
};

const KICK_TYPES = new Set(['kickoff', 'onside_kick', 'free_kick']);

const DRIVE_RESULT_LABELS: Record<string, string> = {
	touchdown: 'Touchdown',
	field_goal: 'Field Goal',
	missed_field_goal: 'Missed FG',
	punt: 'Punt',
	turnover: 'Turnover',
	interception: 'Interception',
	fumble: 'Fumble',
	downs: 'Downs',
	safety: 'Safety',
	end_of_half: 'End of Half',
	end_of_game: 'End of Game',
};

type DisplayDrive = {
	key: string;
	teamId: string;
	period: number;
	plays: OtnPublicPlay[];
	result?: string | null;
};

const deriveDriveResult = (plays: OtnPublicPlay[]): string | null => {
	const scoring = [...plays].reverse().find((p) => p.points > 0);
	if (scoring?.points === 3) return 'field_goal';
	if (scoring && scoring.points >= 6) return 'touchdown';
	const last = plays[plays.length - 1];
	if (!last) return null;
	if (last.play_type === 'punt') return 'punt';
	if (last.play_type === 'interception') return 'interception';
	return null;
};

/**
 * The server's drives (TECHENG-1076): its grouping and results, with this page's copy of each
 * play. Null if it names a play the page doesn't have yet; the hook re-fetches in that case.
 */
const drivesFromOutline = (outline: OtnDriveOutline[], plays: OtnPublicPlay[]): DisplayDrive[] | null => {
	const byId = new Map(plays.map((p) => [p.id, p]));
	const drives: DisplayDrive[] = [];
	for (const d of outline) {
		const drivePlays = d.play_ids.map((id) => byId.get(id));
		if (drivePlays.some((p) => !p)) return null;
		if (!drivePlays.length) continue;
		drives.push({
			key: d.play_ids[0],
			teamId: d.offense_otn_team_id,
			period: d.start_period,
			plays: drivePlays as OtnPublicPlay[],
			result: d.result ?? null,
		});
	}
	return drives;
};

/**
 * Fallback for an API that sends plays without an outline. Groups the flat play list into
 * drives: a new drive starts whenever the team on offense changes. A kickoff opens the receiving team's drive, so its row sits with the drive it
 * started. Server-provided drive results are kept for drives whose first play matches.
 */
const groupIntoDrives = (plays: OtnPublicPlay[], game: OtnGame, serverDrives: OtnDrive[]): DisplayDrive[] => {
	const teamIds = (game.otn_games_otn_teams ?? []).map((t) => t.otn_team_id);
	const otherTeam = (id: string) => teamIds.find((t) => t !== id) ?? id;
	const serverResultByFirstPlay = new Map(serverDrives.map((d) => [d.plays[0]?.id, d.result]));

	const drives: DisplayDrive[] = [];
	for (const play of plays) {
		const driveTeam = KICK_TYPES.has(play.play_type) ? otherTeam(play.offense_otn_team_id) : play.offense_otn_team_id;
		const current = drives[drives.length - 1];
		if (!current || current.teamId !== driveTeam || KICK_TYPES.has(play.play_type)) {
			drives.push({ key: play.id, teamId: driveTeam, period: play.period, plays: [play] });
		} else {
			current.plays.push(play);
		}
	}
	return drives.map((d) => ({
		...d,
		result: serverResultByFirstPlay.get(d.plays[0]?.id) ?? deriveDriveResult(d.plays),
	}));
};

/** Live play-by-play, newest drive first, plays in order within each drive. */
const PlayByPlay = ({ game, initialDrives, preview = false }: PlayByPlayProps) => {
	const { plays, outline } = useLivePlays(game.id, initialDrives, true, preview);
	const drives = useMemo(
		() => (outline && drivesFromOutline(outline, plays)) ?? groupIntoDrives(plays, game, initialDrives),
		[outline, plays, game, initialDrives],
	);

	if (!drives.length) return null;

	const teams = game.otn_games_otn_teams ?? [];
	const teamFor = (id?: string | null) => teams.find((t) => t.otn_team_id === id)?.otn_team;

	return (
		<section className="otn-section-border flex flex-col gap-6 bg-black px-6 py-6" data-testid="play-by-play">
			<div className="border-b-[2px] border-b-[#333] pb-6">
				<h2 className="otn-heading">Play-by-Play</h2>
			</div>
			<div className="flex flex-col gap-6">
				{[...drives].reverse().map((drive) => {
					const team = teamFor(drive.teamId);
					const resultLabel = drive.result ? (DRIVE_RESULT_LABELS[drive.result] ?? drive.result) : null;
					return (
						<div key={drive.key} className="flex flex-col gap-2">
							<div className="bg-otnBlack flex items-center gap-3 rounded-[8px] px-4 py-2">
								{team?.logo_path && (
									<div className="relative h-6 w-6 shrink-0">
										<Image
											src={`${Urls.OvertimeImagesBaseUrl}${team.logo_path}`}
											fill
											alt={`${team.name || 'Team'} logo`}
										/>
									</div>
								)}
								<span className="text-[13px] leading-[150%] font-extrabold uppercase">
									{team?.team_code ?? team?.name ?? 'Drive'}
								</span>
								<span className="text-otnGray text-[11px] leading-[150%] uppercase">
									{periodLabel(drive.period)} · {drive.plays.length} {drive.plays.length === 1 ? 'play' : 'plays'}
								</span>
								{resultLabel && (
									<span className="text-otnLime ml-auto text-[11px] leading-[150%] font-extrabold uppercase">
										{resultLabel}
									</span>
								)}
							</div>
							<ol className="flex flex-col divide-y divide-[#222]">
								{drive.plays.map((play) => {
									const offense = teamFor(play.offense_otn_team_id);
									const defense = teams.find((t) => t.otn_team_id !== play.offense_otn_team_id)?.otn_team;
									const down = downAndDistanceLabel(play.start_down, play.start_distance);
									const spot = ballOnLabel(play.start_yard_line, offense?.team_code, defense?.team_code);
									const clock = optionalClockLabel(play.clock_ms);
									const isScoring = play.points > 0;
									return (
										<li
											key={play.id}
											className={cn(
												'flex flex-col gap-1 px-4 py-2 text-[13px] leading-[150%] md:flex-row md:gap-4',
												isScoring && 'border-l-otnLime border-l-[3px]',
											)}
										>
											<span className="text-otnGray shrink-0 text-[11px] uppercase md:w-[150px]">
												{[down && spot ? `${down} at ${spot}` : (down ?? spot), clock].filter(Boolean).join(' · ')}
											</span>
											<span className={cn('min-w-0 flex-1', isScoring && 'font-extrabold')}>{play.description}</span>
										</li>
									);
								})}
							</ol>
						</div>
					);
				})}
			</div>
		</section>
	);
};

export default PlayByPlay;
